import {
    getAllBranches,
    getBranchData,
    getCurrentBranch,
    regionalizePath,
    type BranchData,
} from '@/lib/config/branches-config';
import { DEFAULT_BRANCH_CODE, siteUrlForBranch } from '@/lib/config/site-host-env';

const BRAND_NAME = 'ЗМК Континенталь';
const BRAND_DESCRIPTION =
    'Завод металлоконструкций и металлоизделий. Проектирование, изготовление и монтаж под ключ.';
/**
 * Origin сайта без завершающего слэша. Без аргумента — хост филиала по
 * умолчанию (Organization/WebSite); с кодом — хост этого филиала (в режиме
 * BRANCH_ROUTING=host у msk свой поддомен). См. lib/config/site-host-env.ts.
 */
export function getSiteUrl(branchCode: string = DEFAULT_BRANCH_CODE): string {
    return siteUrlForBranch(branchCode);
}

/** Стабильный идентификатор юрлица. Одна компания — один @id на всём сайте. */
export function organizationId(): string {
    return `${getSiteUrl()}/#organization`;
}

/** Ссылка на юрлицо для publisher / seller / provider вместо анонимной копии. */
export function organizationRef(): { '@id': string } {
    return { '@id': organizationId() };
}

/**
 * Корневой URL региона. Для spb это `/`, для остальных — `/geo/{code}/`.
 * `regionalizePath` уже гарантирует завершающий слэш, добавлять свой нельзя.
 */
function branchUrl(branchCode: string): string {
    return `${getSiteUrl(branchCode)}${regionalizePath('/', branchCode)}`;
}

/** Идентификатор конкретной площадки: по одному на регион. */
export function localBusinessId(branchCode: string): string {
    return `${branchUrl(branchCode)}#localbusiness`;
}

/**
 * Юрлицо. Адрес здесь юридический (СПб) и от региона страницы не зависит:
 * ИНН, ОГРН и юрадрес у обоих филиалов одни и те же. Территорию обслуживания
 * описывают дочерние LocalBusiness, поэтому areaServed тут намеренно нет.
 */
export function buildOrganizationNode() {
    const siteUrl = getSiteUrl();
    const legalBranch = getBranchData('spb') || getCurrentBranch();
    const requisites = legalBranch?.requisites;

    return {
        '@type': 'Organization',
        '@id': organizationId(),
        name: BRAND_NAME,
        ...(requisites?.companyName && { legalName: requisites.companyName }),
        url: siteUrl,
        logo: `${siteUrl}/project/logo.svg`,
        image: `${siteUrl}/project/logo.svg`,
        description: BRAND_DESCRIPTION,
        ...(requisites?.inn && { taxID: requisites.inn }),
        address: {
            '@type': 'PostalAddress',
            streetAddress: requisites?.legalAddress || legalBranch?.seo?.streetAddress,
            addressLocality: legalBranch?.seo?.addressLocality,
            ...(legalBranch?.seo?.addressRegion && { addressRegion: legalBranch.seo.addressRegion }),
            ...(legalBranch?.seo?.postalCode && { postalCode: legalBranch.seo.postalCode }),
            addressCountry: 'RU',
        },
        contactPoint: {
            '@type': 'ContactPoint',
            telephone: legalBranch?.phone,
            email: legalBranch?.email,
            contactType: 'sales',
            availableLanguage: 'Russian',
        },
        // Ссылки на все площадки: сам узел площадки отдаётся на страницах
        // своего региона, @id — глобальный идентификатор, а не якорь страницы.
        location: getAllBranches().map((branch) => ({ '@id': localBusinessId(branch.code) })),
        ...(legalBranch?.telegram && { sameAs: [legalBranch.telegram] }),
        identifier: [
            ...(requisites?.ogrn ? [{ '@type': 'PropertyValue', propertyID: 'ОГРН', value: requisites.ogrn }] : []),
            ...(requisites?.kpp ? [{ '@type': 'PropertyValue', propertyID: 'КПП', value: requisites.kpp }] : []),
        ],
    };
}

/**
 * Площадка конкретного региона. Именно этот узел несёт географию:
 * адрес региона, часы работы и areaServed без «Россия».
 */
export function buildLocalBusinessNode(branchCode: string) {
    const branch: BranchData | null = getBranchData(branchCode);
    const seo = branch?.seo;

    if (!branch || !seo) {
        return null;
    }

    const siteUrl = getSiteUrl();

    return {
        '@type': 'LocalBusiness',
        '@id': localBusinessId(branch.code),
        name: `${BRAND_NAME} — ${branch.name}`,
        description: BRAND_DESCRIPTION,
        url: branchUrl(branch.code),
        image: `${siteUrl}/project/logo.svg`,
        parentOrganization: organizationRef(),
        address: {
            '@type': 'PostalAddress',
            streetAddress: seo.streetAddress,
            addressLocality: seo.addressLocality,
            addressRegion: seo.addressRegion,
            ...(seo.postalCode && { postalCode: seo.postalCode }),
            addressCountry: 'RU',
        },
        ...(seo.geo && {
            geo: {
                '@type': 'GeoCoordinates',
                latitude: seo.geo.latitude,
                longitude: seo.geo.longitude,
            },
        }),
        telephone: branch.phone,
        email: branch.email,
        openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: seo.openingHours.dayOfWeek,
            opens: seo.openingHours.opens,
            closes: seo.openingHours.closes,
        },
        areaServed: seo.areaServed.map((area) => ({
            '@type': area['@type'],
            name: area.name,
        })),
    };
}

/**
 * WebSite без potentialAction: SearchAction указывал на /search, который закрыт
 * в robots.txt, а сам сайтлинк-серчбокс Google отключил в ноябре 2024.
 */
export function buildWebSiteNode() {
    const siteUrl = getSiteUrl();

    return {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        name: BRAND_NAME,
        url: siteUrl,
        inLanguage: 'ru-RU',
        publisher: organizationRef(),
    };
}

/** Единый граф для рутового layout: одна разметка вместо трёх разрозненных. */
export function buildSiteJsonLd(branchCode: string) {
    const graph = [buildOrganizationNode(), buildLocalBusinessNode(branchCode), buildWebSiteNode()]
        .filter(Boolean);

    return {
        '@context': 'https://schema.org',
        '@graph': graph,
    };
}
